/**
 * AGP bespoke footer — 2026 redesign handoff: near-black, four columns
 * (brand + tagline + social · Navigation · Products · Contact) over a darker
 * bottom bar.
 *
 * Replaces the shared <Footer/> on the six bespoke public pages only; the
 * transactional routes (cart/checkout/product detail) keep the shared one.
 *
 * Copy is sourced from the CMS where the admin can meaningfully edit it — brand
 * name, blurb, copyright, and the social links — so a Storefront-tab edit still
 * lands here. The column link lists are the handoff's, in code, because they
 * point at the bespoke routes this folder owns.
 */
import { Link } from 'react-router-dom';
import { AGP_SITE } from '@mlm/shared';
import { useBrand, useStorefrontContent } from '../../../storefront/lib/hooks';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { SOCIAL, TAGLINE } from './tokens';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/storefront' },
  { label: 'About Us', to: '/about-us' },
  { label: 'Testimonials', to: '/testimonials' },
  { label: 'Policies', to: '/policies' },
  { label: 'Contact Us', to: '/contact' },
];

const PRODUCT_LINKS = [
  'Java Green Coffee',
  'Choco Moringga Plus',
  'Java Black Coffee',
  'Berry Vibe',
  'Starter Packages',
];

/** Fallback socials when the tenant hasn't configured any in the CMS. */
const FALLBACK_SOCIAL = [
  { platform: 'Facebook', url: SOCIAL.facebook },
  { platform: 'YouTube', url: SOCIAL.youtube },
  { platform: 'Instagram', url: SOCIAL.instagram },
];

export function AgpFooter(): JSX.Element {
  const brand = useBrand();
  const { data } = useStorefrontContent();
  // Contact details are Site Pages fields (contact.office.*) so an edit there
  // updates the Contact page AND this footer together.
  const sc = useSiteContent(AGP_SITE);
  const office = (k: string) => sc.text(`contact.office.${k}`);

  // CMS wins; the handoff's defaults fill the gaps. `useStorefrontContent` has
  // already expanded {brand}/{company}/{year} tokens.
  const social = data.social.length > 0 ? data.social : FALLBACK_SOCIAL;
  const blurb =
    data.footer.brandBlurb ||
    `${brand.companyName} — a direct-selling wellness company committed to building health, creating opportunities, and transforming lives across the globe.`;
  const copyright =
    data.footer.copyrightText || `© ${new Date().getFullYear()} ${brand.companyName}`;

  return (
    <>
      <footer className="agp-footer">
        <div>
          <div className="agp-footer-wordmark">
            {/* Match the nav: pick out the middle word in leaf green when the brand
                is the three-word "Alpha Global Prestige"; otherwise render plain. */}
            {(() => {
              const parts = brand.companyName.split(' ');
              if (parts.length !== 3) return brand.companyName;
              return (
                <>
                  {parts[0]} <span>{parts[1]}</span> {parts[2]}
                </>
              );
            })()}
          </div>
          <div className="agp-footer-tagline">&ldquo;{TAGLINE}&rdquo;</div>
          <p className="agp-footer-desc">{blurb}</p>
          <div className="agp-footer-social">
            {social.map((s) => (
              <a key={s.platform} href={s.url} target="_blank" rel="noreferrer noopener">
                {s.platform}
              </a>
            ))}
          </div>
        </div>

        <div>
          <div className="agp-footer-heading">Navigation</div>
          <ul className="agp-footer-links">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="agp-footer-heading">Products</div>
          <ul className="agp-footer-links">
            {PRODUCT_LINKS.map((label) => (
              <li key={label}>
                {/* The catalogue is the real product surface — these are entry points to it. */}
                <Link to="/storefront">{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="agp-footer-heading">Contact</div>
          <div className="agp-footer-contact">
            <span className="agp-footer-contact-label">T</span>
            <span>{office('phone')}</span>
          </div>
          <div className="agp-footer-contact">
            <span className="agp-footer-contact-label">E</span>
            <span>{office('email')}</span>
          </div>
          <div className="agp-footer-contact">
            <span className="agp-footer-contact-label">W</span>
            <span>{office('website')}</span>
          </div>
          <div className="agp-footer-contact">
            <span className="agp-footer-contact-label">A</span>
            <span>
              {office('address')}, {office('addressLine2')}
            </span>
          </div>
        </div>
      </footer>

      <div className="agp-footer-bottom">
        <span>{copyright}. All rights reserved.</span>
        <span>
          <Link to="/policies">Privacy Policy</Link>
          &nbsp;&middot;&nbsp;
          <Link to="/policies">Terms of Use</Link>
        </span>
      </div>
    </>
  );
}
